import type {
  DensitySnapshot,
  DensityStateView,
  InstrumentRef,
  InstrumentStatusView,
  LockedCard,
  SituationView,
  WrappedCoin,
} from "@screener/protocol";
import { LockedState } from "@screener/uikit/components/locked-state";
import { cn } from "@screener/uikit/lib/utils";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ConnectionStatus } from "@/hooks/use-ipc-connection";
import { hashOfSettingsReason } from "@/hooks/use-screen";
import { pluralForm, tickerLabel } from "@/lib/labels";
import { usePlanGate } from "@/lib/plan-gate-context";
import type { TerminalNotice } from "@/terminal/terminal-action";
import { TerminalControlsContext, type TerminalSettingsHint } from "@/terminal/terminal-controls";
import { type LiveMarket, liveMarketsOf } from "@/terminal/terminal-settings-draft";
import { useTerminalSession } from "@/terminal/use-terminal-session";
import { type ActionNotice, nextActionNotice } from "./action-notice";
import {
  CARD_PIN_RELOAD_FAILED_TEXT,
  type CardPinNotice,
  cardExchangeBase,
  cardPinClickIntent,
  cardPinNoticeText,
  cardPinOutcomeNoticeKind,
  clearCardPinNoticeForKey,
} from "./card-pin-labels";
import { useCardRowOrder } from "./card-row-order";
import { buildCards, type CardRowOrder, instrumentKey, type PinnedCards } from "./cards";
import { shownRows } from "./cards-freeze";
import { CardsFrostFrame } from "./cards-frost-layer";
import { type CopyTickerOutcome, copyTicker, tickerForClipboard } from "./copy-ticker";
import { samePinMatch } from "./density-card-row";
import { DensityCards } from "./density-cards";
import { DensityCoinTable } from "./density-coin-table";
import { DensityDetailPanel } from "./density-detail-panel";
import { useDensityFilters } from "./density-filters-storage";
import { DensityTable } from "./density-table";
import {
  type DensityFilters,
  type DensitySort,
  type DensitySortKey,
  defaultSortDirection,
  favoritesFilterUnavailable,
  filterRows,
  isUnconfirmed,
  marketKey,
  type ScreenMode,
  sortRows,
} from "./derive";
import { DismissibleErrorBanner } from "./dismissible-error-banner";
import { activeDisplayMinAgeSec, type DisplayMinAgeState } from "./display-min-age";
import type { DisplayMinimumsState } from "./display-minimums";
import { isWholeCoinFavorite } from "./favorite-match";
import {
  clearFavoriteNoticeForBase,
  type FavoriteToggleNotice,
  favoriteToggleNotice,
  favoriteToggleNoticeText,
} from "./favorite-notice";
import { FilterPanel } from "./filter-panel";
import { armFocusHandoff, disarmFocusHandoff } from "./focus-handoff";
import {
  clearHideNoticeForBase,
  type HideCoinNotice,
  hideCoinNotice,
  hideCoinNoticeText,
} from "./hidden-coin-notice";
import {
  type HiddenCoinsState,
  hiddenBasesOf,
  isHiddenBase,
  withoutHiddenOutcomes,
  withoutHiddenPinned,
  withoutHiddenRows,
  withoutHiddenSituations,
} from "./hidden-match";
import { filterLockedCards, type GridCard } from "./locked-cards";
import { OutcomeRibbon } from "./outcome-ribbon";
import type { RowPinMatch } from "./pin-match";
import { matchPinsToRows } from "./pin-match";
import { createScreenCounts, type ScreenCounts } from "./screen-counts";
import {
  retainExpandedMembers,
  type SituationRow,
  type SituationRowsResult,
  situationRows,
  sortSituationRows,
  toggleExpandedMembers,
} from "./situation-derive";
import { SituationList } from "./situation-list";
import type { StreamState } from "./stream-state";
import { type CardPinsState, useCardPins } from "./use-card-pins";
import { useCardsFreeze } from "./use-cards-freeze";
import { useDensityDetail } from "./use-density-detail";
import type { StreamStore } from "./use-density-stream";
import type { DisplayMinAgeOutcome } from "./use-display-min-age";
import type { UseDisplayMinimumsResult } from "./use-display-minimums";
import type { FavoritesState } from "./use-favorites";
import { useFavorites } from "./use-favorites";
import type { HiddenCoinOutcome } from "./use-hidden-coins";
import { useNotes } from "./use-notes";
import { useNowMs } from "./use-now";
import { usePins } from "./use-pins";
import { sameStringList, useStableCallback, useStableValue } from "./use-stable";
import { useStreamSelector } from "./use-stream-selector";

/**
 * Открытая в панели плотность: из состава потока, но не скрытой монеты. Скрытая
 * монета уходит с экрана целиком (§75 п. 4), и панель закрывается вместе с ней —
 * тем же исходом, что и уход плотности из состава (§9 1.9).
 */
export function openDensityRow(
  densities: Record<string, DensitySnapshot>,
  openId: string | null,
  hidden: ReadonlySet<string>,
): DensitySnapshot | null {
  if (openId === null) {
    return null;
  }
  const row = densities[openId];
  if (row === undefined || isHiddenBase(row.instrument.base, hidden)) {
    return null;
  }
  return row;
}

/**
 * Сообщение о неудаче закрепления §6.7: два разных исхода читаются
 * по-разному, а не сводятся к одному молчанию (review stage5-notes-and-pin,
 * п. 2). «Такого закрепления уже нет» отличается от «повторите» — в первом
 * случае повторное нажатие бессмысленно, во втором осмысленно.
 */
export type PinToggleNotice = "retry" | "gone" | null;

export function pinToggleNotice(
  status: "ok" | "invalid" | "not-found" | "unavailable",
): PinToggleNotice {
  if (status === "ok") {
    return null;
  }
  if (status === "not-found") {
    return "gone";
  }
  return "retry";
}

/**
 * Строка не отправляет второй запрос закрепления, пока не завершился первый
 * (review stage5-notes-and-pin, п. 5): повторное нажатие во время
 * перечитывания списка не имеет права превратить снятие в повторную
 * постановку.
 */
export function canSendPinToggle(pendingIds: ReadonlySet<string>, densityId: string): boolean {
  return !pendingIds.has(densityId);
}

/**
 * Что делает нажатие звёздочки избранного, по состоянию списка и занятости
 * монеты (review stage6-favorites, второй круг, п. 1). Список ещё не
 * прочитан (`favoriteBases === null`) — клиент не знает текущего состояния
 * монеты, и писать нечего: нажатие вместо записи перечитывает список.
 * Читается кнопка активной именно поэтому — она не бесполезна, а действует
 * иначе. Список прочитан, но у монеты уже есть запрос в пути — нажатие
 * игнорируется, тот же приём, что и у `canSendPinToggle`.
 */
export type FavoriteClickIntent =
  | { kind: "reload" }
  | { kind: "toggle"; currentlyFavorite: boolean }
  | { kind: "ignore" };

export function favoriteClickIntent(
  favoriteBases: ReadonlySet<string> | null,
  pendingBases: ReadonlySet<string>,
  base: string,
): FavoriteClickIntent {
  if (favoriteBases === null) {
    return { kind: "reload" };
  }
  if (pendingBases.has(base)) {
    return { kind: "ignore" };
  }
  return { kind: "toggle", currentlyFavorite: favoriteBases.has(base) };
}

/**
 * Сколько миллисекунд держится сообщение о копировании тикера, прежде чем
 * погаснуть само: сообщение короткоживущее (web-density-table «Отказ буфера
 * обмена назван»), а не требует ручного закрытия, как отказ закрепления.
 */
export const COPY_TICKER_NOTICE_MS = 4000;

/**
 * Сообщение о копировании тикера §9. Несёт исход, а не только текст: успех
 * гаснет сам, отказ остаётся и назван цветом отказа — иначе исход читался бы
 * из одинаково приглушённых слов в общем ряду статус-бара
 * (review stage5-round-price, п. 3).
 *
 * `seq` отличает два одинаковых сообщения подряд: без него повторное
 * копирование того же тикера не меняло бы состояние вовсе.
 */
export type CopyTickerNotice = ActionNotice | null;

/**
 * Текст сообщения о копировании тикера §9 — тем же приёмом, что и
 * `pinToggleNotice`: исход буфера обмена называется человеку словами, а не
 * молчанием, и оба случая недоступности (`copyTicker`) сводятся к одному
 * тексту отказа.
 */
export function copyTickerNotice(outcome: CopyTickerOutcome, symbol: string): string {
  return outcome === "copied" ? `Скопировано: ${symbol}` : "Не удалось скопировать тикер";
}

/**
 * Следующее сообщение по исходу копирования. Вынесено из обработчика, чтобы
 * номер было чем проверить: без него два одинаковых исхода подряд давали бы
 * одно и то же значение состояния, React не перерисовывал бы ничего, и
 * таймер гашения продолжал бы отсчёт от первого нажатия.
 */
export function nextCopyNotice(
  previous: CopyTickerNotice,
  outcome: CopyTickerOutcome,
  ticker: string,
  densityId: string,
): ActionNotice {
  return nextActionNotice(previous, outcome, copyTickerNotice(outcome, ticker), densityId);
}

/**
 * Ряды списка ситуаций §5 для экрана — та же связка отбора и сортировки, что
 * получает таблица плотностей (design.md решение 5): один и тот же `filters`
 * и один и тот же `sort` экрана, без второй копии правил.
 */
export function computeSituationRows(
  situations: SituationView[],
  densities: Record<string, DensitySnapshot>,
  filters: DensityFilters,
  sort: DensitySort,
  favorites: FavoritesState,
  minimums: DisplayMinimumsState,
  minAge: DisplayMinAgeState,
  nowMs: number,
): SituationRowsResult {
  const { rows, unknownCount } = situationRows(
    situations,
    densities,
    filters,
    favorites,
    minimums,
    minAge,
    nowMs,
  );
  return { rows: sortSituationRows(rows, sort), unknownCount };
}

/**
 * Текст счётчика в подвале §9: в режиме ситуаций он называет число ситуаций,
 * а не строк, — иначе одно и то же число читалось бы двояко (этап 5, шаг 5.1).
 * Числительное согласовано с числом (review, пункт 5): «1 строка»,
 * «2 строки», «5 строк» — то же для «ситуация/ситуации/ситуаций».
 */
export function footerCountLabel(
  mode: ScreenMode,
  densityRowCount: number,
  situationRowCount: number,
): string {
  return mode === "situations"
    ? `${situationRowCount} ${pluralForm(situationRowCount, "ситуация", "ситуации", "ситуаций")}`
    : `${densityRowCount} ${pluralForm(densityRowCount, "строка", "строки", "строк")}`;
}

interface DensityListAreaProps {
  mode: ScreenMode;
  /** Личные минимумы §59 и запись минимума монеты — для окна в шапке карточки. */
  displayMinimums: DisplayMinimumsState;
  onPutDisplayMinimum: UseDisplayMinimumsResult["put"];
  onRemoveDisplayMinimum: UseDisplayMinimumsResult["remove"];
  onReloadDisplayMinimums: () => void;
  rows: DensitySnapshot[];
  /** Карточки замороженного вида §77; `null` — вид не заморожен. Только для карточек. */
  frozenCards: readonly GridCard[] | null;
  /** Закрытые карточки тарифа (протокол 12) после фильтров — стоят только в сетке карточек. */
  lockedCards: readonly LockedCard[];
  /** Сколько строк плотностей было до фильтров — для пустых состояний таблицы. */
  totalRows: number;
  situationRows: SituationRow[];
  /** Сколько ситуаций было до фильтров — для пустых состояний списка ситуаций. */
  totalSituations: number;
  /** Ситуации, отпавшие без единого известного участника — см. `SituationList`. */
  unknownSituationCount: number;
  sort: DensitySort;
  /** Порядок строк карточки §9 (1.23) — не используется таблицей и списком ситуаций. */
  rowOrder: CardRowOrder;
  instruments: InstrumentStatusView[];
  /** Перечень обёрнутых монет из приветствия. */
  wrapped: readonly WrappedCoin[];
  /** Индекс баз в написании биржи — тикер строки группы ситуаций (§63). */
  exchangeBases: ReadonlyMap<string, string>;
  awaitingHello: boolean;
  /** «Только избранные» включён, а список избранного не прочитан — общая причина пустоты. */
  favoritesUnavailable: boolean;
  onOpenRow: (row: DensitySnapshot) => void;
  notedBases: Set<string> | null;
  pinMatches: Map<string, RowPinMatch> | null;
  onOpenNotes: (row: DensitySnapshot) => void;
  onTogglePin: (row: DensitySnapshot) => void;
  pendingPinIds: ReadonlySet<string>;
  /**
   * Монеты, отмеченные избранным целиком §6.6 (сопоставление клиента,
   * `isWholeCoinFavorite`). `null` — тот же смысл, что и у `notedBases`:
   * список ещё не прочитан или недоступен.
   */
  favoriteBases: Set<string> | null;
  onToggleFavorite: (row: DensitySnapshot) => void;
  /** Монеты, чьё собственное действие избранного ещё не завершилось. */
  pendingFavoriteBases: ReadonlySet<string>;
  /** Раскрытые группы списка ситуаций — не используются таблицей плотностей. */
  expandedMembers: ReadonlySet<string>;
  onToggleExpand: (row: SituationRow) => void;
  /** Скрыть монету целиком — хоткей `h` строки и значок шапки карточки (§75 п. 2). */
  onHideCoin: (base: string) => void;
  onCopyTicker: (row: DensitySnapshot) => void;
  /** Плотность, по которой сейчас открыта детальная панель — карточка держит её видной. */
  selectedDensityId: string | null;
  /** Закрепления карточек §65 — используются только карточками. */
  pinnedCards: PinnedCards | null;
  cardPinsStatus: CardPinsState["status"];
  pendingCardPinKeys: ReadonlySet<string>;
  cardPinExchangeBases: ReadonlyMap<string, string>;
  onToggleCardPin: (instrument: InstrumentRef) => void;
}

/**
 * Тело экрана §9 — карточки, таблица плотностей или список ситуаций, выбор по
 * режиму (design.md change stage6-density-cards, решение 2; этап 5, решение
 * 5). Все три представления получают один и тот же отбор, порядок и действия
 * строки: режим переставляет только то, что рисуется, а не состояние, которым
 * экран управляет.
 */
export function DensityListArea({
  mode,
  displayMinimums,
  onPutDisplayMinimum,
  onRemoveDisplayMinimum,
  onReloadDisplayMinimums,
  rows,
  frozenCards,
  lockedCards,
  totalRows,
  situationRows: situationRowsProp,
  totalSituations,
  unknownSituationCount,
  sort,
  rowOrder,
  instruments,
  wrapped,
  exchangeBases,
  awaitingHello,
  favoritesUnavailable,
  onOpenRow,
  notedBases,
  pinMatches,
  onOpenNotes,
  onTogglePin,
  pendingPinIds,
  favoriteBases,
  onToggleFavorite,
  pendingFavoriteBases,
  expandedMembers,
  onToggleExpand,
  onHideCoin,
  onCopyTicker,
  selectedDensityId,
  pinnedCards,
  cardPinsStatus,
  pendingCardPinKeys,
  cardPinExchangeBases,
  onToggleCardPin,
}: DensityListAreaProps) {
  const planGate = usePlanGate();
  if (mode === "cards") {
    return (
      <DensityCards
        displayMinimums={displayMinimums}
        onPutMinimum={onPutDisplayMinimum}
        onRemoveMinimum={onRemoveDisplayMinimum}
        onReloadMinimums={onReloadDisplayMinimums}
        frozenCards={frozenCards}
        lockedCards={lockedCards}
        rows={rows}
        totalRows={totalRows}
        sort={sort}
        rowOrder={rowOrder}
        instruments={instruments}
        wrapped={wrapped}
        awaitingHello={awaitingHello}
        favoritesUnavailable={favoritesUnavailable}
        onOpenRow={onOpenRow}
        notedBases={notedBases}
        pinMatches={pinMatches}
        onOpenNotes={onOpenNotes}
        onTogglePin={onTogglePin}
        favoriteBases={favoriteBases}
        onToggleFavorite={onToggleFavorite}
        onHideCoin={onHideCoin}
        onCopyTicker={onCopyTicker}
        selectedDensityId={selectedDensityId}
        exchangeBases={exchangeBases}
        pinned={pinnedCards}
        cardPinsStatus={cardPinsStatus}
        pendingCardPinKeys={pendingCardPinKeys}
        cardPinExchangeBases={cardPinExchangeBases}
        onToggleCardPin={onToggleCardPin}
      />
    );
  }
  if (mode === "coins") {
    return (
      <DensityCoinTable
        rows={rows}
        totalRows={totalRows}
        sort={sort}
        instruments={instruments}
        wrapped={wrapped}
        awaitingHello={awaitingHello}
        favoritesUnavailable={favoritesUnavailable}
        onOpenRow={onOpenRow}
        notedBases={notedBases}
        pinMatches={pinMatches}
        onOpenNotes={onOpenNotes}
        onTogglePin={onTogglePin}
        pendingPinIds={pendingPinIds}
        favoriteBases={favoriteBases}
        onToggleFavorite={onToggleFavorite}
        pendingFavoriteBases={pendingFavoriteBases}
        onHideCoin={onHideCoin}
        onCopyTicker={onCopyTicker}
      />
    );
  }
  if (mode === "situations") {
    // Тариф с лимитом открытых карточек: демон ситуаций не шлёт, и вместо
    // пустого списка клиент называет причину.
    if (planGate.situationsLocked) {
      return <LockedState title="Ситуации" text={planGate.situationsText} />;
    }
    return (
      <SituationList
        rows={situationRowsProp}
        totalSituations={totalSituations}
        unknownCount={unknownSituationCount}
        sort={sort}
        instruments={instruments}
        wrapped={wrapped}
        exchangeBases={exchangeBases}
        awaitingHello={awaitingHello}
        favoritesUnavailable={favoritesUnavailable}
        onOpenRow={onOpenRow}
        notedBases={notedBases}
        pinMatches={pinMatches}
        onOpenNotes={onOpenNotes}
        onTogglePin={onTogglePin}
        pendingPinIds={pendingPinIds}
        favoriteBases={favoriteBases}
        onToggleFavorite={onToggleFavorite}
        pendingFavoriteBases={pendingFavoriteBases}
        expandedMembers={expandedMembers}
        onToggleExpand={onToggleExpand}
        onHideCoin={onHideCoin}
        onCopyTicker={onCopyTicker}
      />
    );
  }
  return (
    <DensityTable
      rows={rows}
      totalRows={totalRows}
      sort={sort}
      instruments={instruments}
      wrapped={wrapped}
      awaitingHello={awaitingHello}
      favoritesUnavailable={favoritesUnavailable}
      onOpenRow={onOpenRow}
      notedBases={notedBases}
      pinMatches={pinMatches}
      onOpenNotes={onOpenNotes}
      onTogglePin={onTogglePin}
      pendingPinIds={pendingPinIds}
      favoriteBases={favoriteBases}
      onToggleFavorite={onToggleFavorite}
      pendingFavoriteBases={pendingFavoriteBases}
      onHideCoin={onHideCoin}
      onCopyTicker={onCopyTicker}
    />
  );
}

function selectDensities(state: StreamState): StreamState["densities"] {
  return state.densities;
}

function selectLocked(state: StreamState): StreamState["locked"] {
  return state.locked;
}

function selectInstruments(state: StreamState): StreamState["instruments"] {
  return state.instruments;
}

function selectSituations(state: StreamState): StreamState["situations"] {
  return state.situations;
}

function selectOutcomes(state: StreamState): StreamState["outcomes"] {
  return state.outcomes;
}

function selectRun(state: StreamState): StreamState["run"] {
  return state.run;
}

function selectWrappedCoins(state: StreamState): StreamState["wrapped_coins"] {
  return state.wrapped_coins;
}

function selectExchangeBases(state: StreamState): StreamState["exchange_bases"] {
  return state.exchange_bases;
}

/**
 * Текст исхода копирования в живой области верхней полосы каркаса. Отдельный
 * `memo`-лист: экран перерисовывается кадрами потока, а исход меняется только
 * по копированию — полоса каркаса не обязана перерисовываться вместе с экраном.
 */
const CopyNoticePortal = memo(function CopyNoticePortal({
  slot,
  notice,
}: {
  slot: HTMLElement | null;
  notice: CopyTickerNotice;
}) {
  if (slot === null) {
    return null;
  }
  return createPortal(
    <span className={notice?.outcome === "failed" ? "text-destructive" : "text-muted-foreground"}>
      {notice?.text ?? ""}
    </span>,
    slot,
  );
});

/**
 * Пауза подписки экрана на поток. Скрытый экран (открыты «Алерты» или
 * «Библиотека») поток не слушает. Исключение — заморожен вид «Карточки» §77:
 * заморозка запоминает последний живой снимок каждой строки, и без подписки
 * строка, ушедшая за время скрытия, показала бы снимок старше последнего.
 */
export function isStreamPaused(active: boolean, frozen: boolean): boolean {
  return !active && !frozen;
}

interface DensityScreenProps {
  /**
   * Хранилище потока: экран читает состояние сам, а родители — нет, чтобы
   * кадры потока перерисовывали экран, а не каркас приложения.
   */
  store: StreamStore;
  /**
   * Экран плотностей виден (открыт он, а не «Алерты» или «Библиотека»). Скрытый
   * экран смонтирован, но поток не слушает (`isStreamPaused`); на активации он
   * один раз догоняет текущее состояние. По умолчанию виден.
   */
  active?: boolean;
  /** Связь — для приглушения данных при разрыве; сама полоса связи живёт в каркасе (§88 п. 7). */
  connectionStatus: ConnectionStatus;
  /**
   * Узел области исхода копирования в верхней полосе каркаса: исход живёт
   * здесь, а рисуется там порталом (design.md stage9-redesign-chrome, Д4).
   * `null`, пока полоса не смонтирована.
   */
  noticeSlot: HTMLElement | null;
  /** Сессия истекла на запросном канале — тот же исход, что у потока. */
  onSessionInvalid: () => void;
  /**
   * Личные минимумы объёма §59. Список читает и правит каркас (один экземпляр
   * хука на окно и экран): правка в окне сразу меняет отбор здесь.
   */
  displayMinimums: DisplayMinimumsState;
  /** Открыть окно всех минимумов (кнопка «Мин. объём» полосы фильтров). */
  onOpenDisplayMinimums: () => void;
  /** Запись и снятие минимума монеты из окна карточки: тот же экземпляр хука, что у общего окна. */
  onPutDisplayMinimum: UseDisplayMinimumsResult["put"];
  onRemoveDisplayMinimum: UseDisplayMinimumsResult["remove"];
  /** Перечитать список минимумов — «Повторить» окна карточки. */
  onReloadDisplayMinimums: () => void;
  /**
   * Скрытые монеты §6.10. Список читает и правит каркас (один экземпляр хука на
   * раздел «Скрытые монеты» страницы «Настройки» и экран): возврат там сразу
   * меняет отбор здесь.
   * Пока список не `ready`, отбора по скрытию нет — ошибка хранилища не прячет данные.
   */
  hiddenCoins: HiddenCoinsState;
  /** Скрыть монету (§75 п. 2): исход решает, закрывать ли панель и называть ли отказ. */
  onHideCoin: (base: string) => Promise<HiddenCoinOutcome>;
  /** Монеты, чьё собственное действие скрытия или возврата ещё в пути. */
  pendingHiddenBases: ReadonlySet<string>;
  /**
   * Вид экрана §9 — приходит из адреса (stage8-sidebar-views, Д2), выбирается
   * подпунктами боковой панели. Смена вида не трогает ни один кусок состояния
   * ниже: он ими не управляет.
   */
  mode: ScreenMode;
  /**
   * Время до показа §82. Читает и правит каркас (`useDisplayMinAge`). Тикер
   * часов экран подключает только при заданной настройке.
   */
  displayMinAge: DisplayMinAgeState;
  /** Записать время до показа в секундах (окно кнопки полосы фильтров). */
  onSaveDisplayMinAge: (minAgeSec: number) => Promise<DisplayMinAgeOutcome>;
  /** Сбросить время до показа в «не задано». */
  onRemoveDisplayMinAge: () => Promise<DisplayMinAgeOutcome>;
  /** Перечитать время до показа — «Повторить» окна, когда чтение не удалось. */
  onReloadDisplayMinAge: () => void;
  /**
   * Куда экран кладёт источник рынков живых плотностей: страница «Настройки»
   * строит по нему таблицу кодов терминала (design.md stage9-settings-page,
   * Д4). Экран записывает геттер при монтировании и сбрасывает при размонтировании.
   */
  liveMarketsSource: { current: () => readonly LiveMarket[] };
}

/**
 * Экран живой таблицы §9: отбор, порядок и состояния потока. Собирает
 * производные от состояния — хранить отсортированный и отфильтрованный
 * состав отдельно значило бы держать вторую истину рядом с первой.
 */
export function DensityScreen({
  store,
  active = true,
  connectionStatus,
  noticeSlot,
  onSessionInvalid,
  displayMinimums,
  onOpenDisplayMinimums,
  onPutDisplayMinimum,
  onRemoveDisplayMinimum,
  onReloadDisplayMinimums,
  hiddenCoins,
  onHideCoin,
  pendingHiddenBases,
  mode,
  displayMinAge,
  onSaveDisplayMinAge,
  onRemoveDisplayMinAge,
  onReloadDisplayMinAge,
  liveMarketsSource,
}: DensityScreenProps) {
  // Признак заморозки вида «Карточки» §77 берётся из прошлого рендера: сама
  // заморозка считается ниже, из тех самых срезов потока, которые пауза и
  // выключает (хранилища под флаг не заводим).
  const frozenRef = useRef(false);
  const paused = isStreamPaused(active, frozenRef.current);
  // Срезы вместо состояния потока целиком: `applyMessages` сохраняет ссылки на
  // неизменившиеся поля, так что кадр, не задевший срез (`alert_fired`, статус
  // другого поля), экран не будит.
  const densities = useStreamSelector(store, selectDensities, Object.is, paused);
  const instruments = useStreamSelector(store, selectInstruments, Object.is, paused);
  const lockedCards = useStreamSelector(store, selectLocked, Object.is, paused);
  const situations = useStreamSelector(store, selectSituations, Object.is, paused);
  const outcomes = useStreamSelector(store, selectOutcomes, Object.is, paused);
  const run = useStreamSelector(store, selectRun, Object.is, paused);
  const wrappedCoins = useStreamSelector(store, selectWrappedCoins, Object.is, paused);
  const exchangeBases = useStreamSelector(store, selectExchangeBases, Object.is, paused);
  // Часы отбора §82: подписка на тикер — только при заданном времени до показа
  // и только пока экран не на паузе; на паузе держится последнее «сейчас».
  const nowMs = useNowMs(activeDisplayMinAgeSec(displayMinAge) !== null, paused);
  const [sort, setSort] = useState<DensitySort>({ key: "usd", direction: "desc" });
  // Порядок строк карточки §9 (1.23) — запоминается в браузере, переживает
  // перезагрузку (design.md, решение 4); недоступное хранилище не роняет
  // экран, значение остаётся «стакан».
  const [rowOrder, setRowOrder] = useCardRowOrder();
  const screenPlanGate = usePlanGate();
  const [storedFilters, setFilters] = useDensityFilters();
  // Пометок ММ тариф не даёт (Д10): переключатель «Скрыть ММ» заблокирован, и
  // фильтр не применяется, как выключенный. Сохранённое значение не стирается.
  const filters = useMemo(
    () =>
      screenPlanGate.mmApproachesLocked && storedFilters.hideMm
        ? { ...storedFilters, hideMm: false }
        : storedFilters,
    [storedFilters, screenPlanGate.mmApproachesLocked],
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Раскрытые группы списка ситуаций §5 — составом, а не ключом ситуации:
  // ключ §5 не уникален (в режиме `by_pair` биржи в нём нет вовсе), и
  // раскрытие по ключу раскрывало разом все одноимённые группы монеты
  // (найдено первым боевым подъёмом, 22.09.2026). Здесь лежат `density_id`
  // участников раскрытых групп; группа раскрыта, пока хоть один её участник
  // здесь — так раскрытие переживает смену состава, чего и требует
  // web-situation-list («сохраняется, пока ситуация остаётся в списке»).
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  // Панель открыта действием «заметки» со строки §9 — раздел заметок получает
  // фокус сразу, а не при обычном открытии по клику на саму строку.
  const [focusNotesOnOpen, setFocusNotesOnOpen] = useState(false);
  // Строки, чей собственный запрос закрепления ещё не завершился: кнопка
  // недоступна, пока он идёт, и второе нажатие не отправляет второй запрос
  // (review stage5-notes-and-pin, п. 5).
  const [pendingPinIds, setPendingPinIds] = useState<Set<string>>(new Set());
  // Отказ закрепления не имеет права молчать (review stage5-notes-and-pin,
  // п. 2): различаются «повторите» и «такого закрепления уже нет».
  const [pinNotice, setPinNotice] = useState<PinToggleNotice>(null);
  // Отказ записи избранного §6.6 — тот же приём, что у закрепления. Значение
  // читает и общая полоса над списком (действие со строки таблицы), и
  // заголовок детальной панели (web-density-detail «Отказ записи из
  // панели»): полоса над списком скрыта затемнением `Sheet`, когда панель
  // открыта, и панель обязана называть отказ сама.
  const [favoriteNotice, setFavoriteNotice] = useState<FavoriteToggleNotice>(null);
  // Отказ скрытия монеты §75 — тот же приём: полоса над списком и заголовок
  // панели, которая остаётся открытой при неудаче.
  const [hideNotice, setHideNotice] = useState<HideCoinNotice>(null);
  // Исход копирования тикера §9 (web-density-table «Отказ буфера обмена
  // назван») — сообщение рядом с «На связи». Несёт порядковый номер, а не
  // один текст: повторное копирование того же тикера даёт ту же строку,
  // состояние бы не изменилось, и таймер гашения продолжал бы тикать от
  // первого нажатия — сообщение исчезало бы посреди второго
  // (review stage5-round-price, п. 1).
  const [copyNotice, setCopyNotice] = useState<CopyTickerNotice>(null);

  const notes = useNotes({ onSessionInvalid });
  const pins = usePins({ onSessionInvalid });
  const favorites = useFavorites({ onSessionInvalid });
  const cardPins = useCardPins({ onSessionInvalid });
  // Отказ записи закрепления карточки §65 — текст полосы над списком.
  const [cardPinNotice, setCardPinNotice] = useState<CardPinNotice | null>(null);

  // Открытая плотность берётся из состояния потока, а не запоминается снимком:
  // ушла в терминал — исчезла из состава, и панель закрывается вместе со
  // строкой (§9 1.9). По отказу маршрута панель не закрывается: недоступный
  // демон — не исход стены.
  const hidden = useMemo(() => hiddenBasesOf(hiddenCoins), [hiddenCoins]);
  const openRow = openDensityRow(densities, openId, hidden);
  const detail = useDensityDetail({
    densityId: openRow === null ? null : openId,
    onSessionInvalid,
  });
  const handleCloseDetail = useCallback(() => {
    setOpenId(null);
    setFocusNotesOnOpen(false);
  }, []);

  // Уход плотности из состава — тот же исход, что и явное закрытие: `openId`
  // не вправе пережить свою строку и открыть панель заново, когда тот же
  // `density_id` вернётся переподключением или ресинком (§9 1.9).
  useEffect(() => {
    if (openId !== null && openDensityRow(densities, openId, hidden) === null) {
      handleCloseDetail();
    }
  }, [openId, densities, hidden, handleCloseDetail]);

  // Скрытые монеты §75 уходят из общего набора один раз — до фильтров и до
  // построения ситуаций: карточки, «по монете», таблица, ситуации, счётчики и
  // закреплённые карточки получают уже отобранный состав.
  const allRows = useMemo(
    () => withoutHiddenRows(Object.values(densities), hidden),
    [densities, hidden],
  );
  const visibleSituations = useMemo(
    () => withoutHiddenSituations(situations, hidden),
    [situations, hidden],
  );
  const visibleOutcomes = useMemo(
    () => withoutHiddenOutcomes(outcomes, hidden),
    [outcomes, hidden],
  );
  // Рынки бирж потока ключами «<биржа>:<рынок>» (§115): единица отбора бирж.
  const venues = useStableValue(
    useMemo(
      () => [...new Set(instruments.map((entry) => marketKey(entry.instrument)))].sort(),
      [instruments],
    ),
    sameStringList,
  );
  // Числа рядом с отбором считаются до применения фильтров: они говорят,
  // сколько строк отбор даст, а не сколько их осталось после него.
  const rawCounts = useMemo(() => {
    const states: Partial<Record<DensityStateView, number>> = {};
    const venues: Record<string, number> = {};
    for (const row of allRows) {
      // `Detected` считается на фишке `Pending`: она его и показывает (Д10).
      const stage: DensityStateView = isUnconfirmed(row.state) ? "pending" : row.state;
      states[stage] = (states[stage] ?? 0) + 1;
      const key = marketKey(row.instrument);
      venues[key] = (venues[key] ?? 0) + 1;
    }
    return { states, venues };
  }, [allRows]);
  const rows = useMemo(
    () =>
      sortRows(
        filterRows(allRows, filters, favorites.state, displayMinimums, displayMinAge, nowMs),
        sort,
      ),
    [allRows, filters, sort, favorites.state, displayMinimums, displayMinAge, nowMs],
  );
  // Тот же отбор и та же сортировка экрана, что и у таблицы (design.md
  // решение 5) — вторая копия правил здесь не заводится.
  const situationRowsComputed = useMemo(
    () =>
      computeSituationRows(
        visibleSituations,
        densities,
        filters,
        sort,
        favorites.state,
        displayMinimums,
        displayMinAge,
        nowMs,
      ),
    [
      visibleSituations,
      densities,
      filters,
      sort,
      favorites.state,
      displayMinimums,
      displayMinAge,
      nowMs,
    ],
  );
  // «Только избранные» без прочитанного списка — пустой состав с названной
  // причиной (design.md решение 7): один и тот же признак для обоих
  // представлений, потому что переключатель в фильтрах общий для режимов.
  const favoritesUnavailable = useMemo(
    () => favoritesFilterUnavailable(filters, favorites.state),
    [filters, favorites.state],
  );

  const handleToggleExpand = useCallback((row: SituationRow) => {
    setExpandedMembers((previous) => toggleExpandedMembers(previous, row.memberIds));
  }, []);

  // Участник, ушедший из всех ситуаций, не имеет права держать раскрытие
  // вечно: без уборки набор рос бы всё время работы вкладки, а вернувшийся
  // тем же `density_id` ресинк раскрывал бы группу, которую человек уже
  // свернул. Та же причина и тот же приём, что у памяти закреплений ниже.
  //
  // Мера — состав ситуаций, а не наличие снимка: состав §7.6 вправе отставать
  // от снимков на такт, и уборка по снимкам вычёркивала бы ровно тех
  // участников, ради которых раскрытие и держится составом (ревью фронтовой
  // части, 22.09.2026, пункт 1).
  useEffect(() => {
    setExpandedMembers((previous) => retainExpandedMembers(previous, situations));
  }, [situations]);

  // Значки §9 (1.13, 1.18) — сопоставление клиента, не поле снимка: заметка по
  // монете, закрепление по уровню (web-density-table). Считаются от видимых
  // строк — скрытая фильтром строка значка всё равно не покажет. `null` —
  // список ещё не прочитан или недоступен: строка обязана показать
  // «неизвестно», а не уверенно соврать «нет» (review stage5-notes-and-pin,
  // п. 3). Пока список читается, `notes.state`/`pins.state` держат прежние
  // готовые данные (`beforeNotesReload`/`beforePinsReload`) — здесь этот
  // случай неотличим от уже готового списка, и моргания нет.
  const notedBases = useMemo(
    () =>
      notes.state.status === "ready" ? new Set(notes.state.notes.map((entry) => entry.base)) : null,
    [notes.state],
  );
  // Монеты, отмеченные избранным целиком §6.6 — по самому списку избранного,
  // а не по видимым строкам: запись на монету без единой живой строки прямо
  // сейчас остаётся в силе, и звёздочка обязана загореться, как только строка
  // появится, без перечитывания списка.
  const favoriteBases = useMemo(() => {
    if (favorites.state.status !== "ready") {
      return null;
    }
    const candidates = new Set(favorites.state.favorites.map((entry) => entry.base));
    const wholeCoin = new Set<string>();
    for (const base of candidates) {
      if (isWholeCoinFavorite(base, favorites.state.favorites)) {
        wholeCoin.add(base);
      }
    }
    return wholeCoin;
  }, [favorites.state]);

  // Закрепления карточек §65. Пока список не прочитан или состав потока не
  // получен (приветствия нет), закреплённых карточек нет: иначе каждая
  // закреплённая сказала бы «плотностей нет» ложно (design Д7).
  const cardPinnedKeys = useMemo(
    () =>
      new Set(
        cardPins.state.status === "ready"
          ? cardPins.state.pins.map((pin) => instrumentKey(pin.instrument))
          : [],
      ),
    [cardPins.state],
  );
  const pinnedCards = useMemo((): PinnedCards | null => {
    if (cardPins.state.status !== "ready" || run === null) {
      return null;
    }
    // Скрытие сильнее закрепления (§75 п. 4): закрепление в базе остаётся, а
    // карточка скрытой монеты не воскресает пустой.
    return withoutHiddenPinned(
      {
        keys: new Map(
          cardPins.state.pins.map((pin) => [instrumentKey(pin.instrument), pin.instrument]),
        ),
        allRows,
      },
      hidden,
    );
  }, [cardPins.state, run, allRows, hidden]);
  // Заморозка вида «Карточки» §77. «Жива» плотность — по всем живым строкам
  // потока до фильтров экрана (`allRows`; скрытые монеты из них уже убраны),
  // счёт новых — по строкам после фильтров (`rows`). Сортировка, фильтры,
  // личный минимум, порядок строк и вид размораживают вид сами (`deps`).
  // Закрытые карточки тарифа подчиняются фильтрам экрана (Д10).
  const visibleLockedCards = useMemo(
    () => filterLockedCards(lockedCards.values(), filters),
    [lockedCards, filters],
  );
  const cardsFreeze = useCardsFreeze({
    deps: { sort, filters, displayMinimums, rowOrder, mode },
    buildCurrent: () => buildCards(rows, sort, rowOrder, pinnedCards),
    lockedNow: visibleLockedCards,
    liveRows: allRows,
    visibleRows: rows,
    hidden,
    pinnedKeys: cardPinnedKeys,
  });
  useLayoutEffect(() => {
    frozenRef.current = cardsFreeze.frozen;
  }, [cardsFreeze.frozen]);
  // Закрепления уровней сопоставляются со строками, которые на экране: в
  // замороженном виде это живые строки снимка (в том числе выпавшие из
  // фильтров) и ушедшие последним снимком, а не только `rows` (§77).
  const pinRows = useMemo(
    () => (cardsFreeze.cards === null ? rows : shownRows(cardsFreeze.cards)),
    [cardsFreeze.cards, rows],
  );
  const pinMatches = useMemo(
    () =>
      pins.state.status === "ready" ? matchPinsToRows(pins.state.pins, pinRows, pins.memory) : null,
    [pins.state, pins.memory, pinRows],
  );
  const cardPinExchangeBases = useMemo(() => {
    const bases = new Map<string, string>();
    if (cardPins.state.status === "ready") {
      for (const pin of cardPins.state.pins) {
        if (pin.exchange_base !== null) {
          bases.set(instrumentKey(pin.instrument), pin.exchange_base);
        }
      }
    }
    return bases;
  }, [cardPins.state]);

  // Память вкладки о плотности, с которой поставлено закрепление, не должна
  // пережить саму плотность (design.md решение 1a): состав живых `density_id`
  // приходит потоком, а не запросным сопоставлением.
  useEffect(() => {
    pins.forgetDeadDensities(new Set(Object.keys(densities)));
  }, [densities, pins.forgetDeadDensities]);

  // Закрепление открытой плотности: объект совпадения пересобирается на каждый
  // кадр, панель держит прежнюю ссылку, пока значение то же.
  const openPinMatch = useStableValue(
    openRow === null
      ? undefined
      : pinMatches === null
        ? ("unknown" as const)
        : pinMatches.get(openRow.density_id),
    samePinMatch,
  );

  const handleOpenNotes = useCallback((row: (typeof rows)[number]) => {
    setFocusNotesOnOpen(true);
    setOpenId(row.density_id);
  }, []);

  // Обработчики строк имеют постоянную ссылку, а читают актуальное состояние:
  // строки обёрнуты в `memo`, и ссылка, зависящая от сопоставлений на каждый
  // кадр потока, перерисовывала бы все строки (`useStableCallback`).
  const handleTogglePin = useStableCallback((row: DensitySnapshot) => {
    // Второй клик, пока первый ещё в пути, не отправляет второй запрос
    // (review stage5-notes-and-pin, п. 5): недоступность кнопки в разметке
    // (`pinPending`) — крайний случай той же гонки, а этот `return` —
    // основной.
    if (!canSendPinToggle(pendingPinIds, row.density_id)) {
      return;
    }
    const existing = pinMatches?.get(row.density_id)?.pin;
    setPinNotice(null);
    setPendingPinIds((previous) => new Set(previous).add(row.density_id));
    pins
      .toggle(
        existing,
        { instrument: row.instrument, side: row.side, price: row.anchor_price },
        row.density_id,
      )
      .then((status) => {
        // Отказ не имеет права проглатываться молча (review
        // stage5-notes-and-pin, п. 2): «нет такого» и «повторите» — разные
        // исходы, и таблица называет их разными словами.
        setPinNotice(pinToggleNotice(status));
      })
      .finally(() => {
        setPendingPinIds((previous) => {
          const next = new Set(previous);
          next.delete(row.density_id);
          return next;
        });
      });
  });

  // Второй клик по той же монете, пока первый запрос ещё в пути, игнорируется
  // — тот же приём, что у закрепления: `pendingBases` хука уже несёт это
  // состояние, второй набор состояния экрану заводить незачем. Непрочитанный
  // список избранного не отклоняет нажатие, а меняет его смысл (review
  // stage6-favorites, второй круг, п. 1): клиент не знает, ставить или
  // снимать, и вместо записи нажатие перечитывает список.
  const handleToggleFavorite = useStableCallback((row: DensitySnapshot) => {
    const base = row.instrument.base;
    const intent = favoriteClickIntent(favoriteBases, favorites.pendingBases, base);
    if (intent.kind === "ignore") {
      return;
    }
    if (intent.kind === "reload") {
      void favorites.reload();
      return;
    }
    // Старт действия гасит только уведомление о своей монете (review,
    // второй круг, п. 3): отказ, показанный по другой монете, не имеет
    // права исчезнуть из-за нажатия здесь.
    setFavoriteNotice((previous) => clearFavoriteNoticeForBase(previous, base));
    favorites.toggle(base, intent.currentlyFavorite).then((status) => {
      // Отказ назван и в общей полосе над списком, и в заголовке панели
      // (web-density-detail «Отказ записи из панели») — одно и то же
      // значение читают оба места, панель показывает его только если
      // отказ о её собственной монете (review, п. 5). Успех тоже гасит
      // только своё уведомление — та же защита, что и на старте.
      const outcome = favoriteToggleNotice(status, base);
      setFavoriteNotice((previous) => outcome ?? clearFavoriteNoticeForBase(previous, base));
    });
  });

  // Скрытие монеты §75 п. 2: без подтверждения, повтор до ответа игнорируется.
  // Успех правит список хука, отбор срабатывает сам, а панель закрывается тем же
  // отбором (`openDensityRow`). Отказ назван полосой и заголовком панели.
  const handleHideCoin = useStableCallback((base: string) => {
    if (pendingHiddenBases.has(base)) {
      return;
    }
    setHideNotice((previous) => clearHideNoticeForBase(previous, base));
    // Фокус перейдёт на соседнюю строку, когда строка под фокусом уйдёт (Д8);
    // отказ взвод снимает: строка никуда не денется.
    armFocusHandoff();
    onHideCoin(base).then((status) => {
      if (status !== "ok") {
        disarmFocusHandoff();
      }
      const outcome = hideCoinNotice(status, base);
      setHideNotice((previous) => outcome ?? clearHideNoticeForBase(previous, base));
    });
  });

  // Нажатие на значок закрепления карточки (§65): ключ в пути игнорируется,
  // непрочитанный список перечитывается вместо записи, иначе переключение.
  // Отказ записи назван полосой, как у избранного.
  const handleToggleCardPin = useStableCallback((instrument: InstrumentRef) => {
    const key = instrumentKey(instrument);
    const intent = cardPinClickIntent(
      cardPins.state.status,
      cardPins.pendingKeys,
      cardPinnedKeys,
      key,
    );
    if (intent.kind === "ignore") {
      return;
    }
    // Старт и успех гасят только уведомление своего ключа (как у избранного).
    setCardPinNotice((previous) => clearCardPinNoticeForKey(previous, key));
    if (intent.kind === "reload") {
      cardPins.reload().then((read) => {
        if (!read) {
          setCardPinNotice({ key, text: CARD_PIN_RELOAD_FAILED_TEXT });
        }
      });
      return;
    }
    cardPins.toggle(instrument, intent.currentlyPinned).then((outcome) => {
      const kind = cardPinOutcomeNoticeKind(outcome);
      if (kind === null) {
        return;
      }
      const ticker = tickerLabel(
        instrument,
        cardExchangeBase(key, exchangeBases, cardPinExchangeBases),
      );
      setCardPinNotice({ key, text: cardPinNoticeText(ticker, kind) });
    });
  });

  // Гаснет само только удавшееся копирование: отказ буфера — свойство
  // окружения (нет защищённого контекста — не будет и в следующий раз),
  // и исчезнувшая надпись читалась бы как «на этот раз получилось»
  // (review stage5-round-price, пп. 1 и 3). Отказ снимает следующий исход.
  // Зависимость — `seq`, а не текст: два одинаковых сообщения подряд
  // обязаны перезапустить таймер.
  const copyNoticeSeq = copyNotice?.seq ?? null;
  const copyNoticeOutcome = copyNotice?.outcome ?? null;
  useEffect(() => {
    if (copyNoticeSeq === null || copyNoticeOutcome !== "copied") {
      return;
    }
    const timer = setTimeout(() => setCopyNotice(null), COPY_TICKER_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [copyNoticeSeq, copyNoticeOutcome]);

  const handleCopyTicker = useCallback((row: DensitySnapshot) => {
    const ticker = tickerForClipboard(row);
    // `navigator.clipboard` отсутствует вне защищённого контекста (design.md
    // «Риски»): `copyTicker` сводит и это, и отказ `writeText` к одному
    // исходу — «failed» — и разница читателю не видна, только названа.
    void copyTicker(ticker, navigator.clipboard).then((outcome) => {
      setCopyNotice((previous) => nextCopyNotice(previous, outcome, ticker, row.density_id));
    });
  }, []);

  // Исход отправки в терминал (§10 1.36, §89) — тем же сообщением и тем же
  // таймером гашения, что и «Скопировано: …»: успех гаснет сам, отказ остаётся.
  const handleTerminalNotice = useCallback((notice: TerminalNotice, densityId: string) => {
    setCopyNotice((previous) =>
      nextActionNotice(
        previous,
        notice.tone === "ok" ? "copied" : "failed",
        notice.text,
        densityId,
      ),
    );
  }, []);

  // Настройки терминала живут на странице «Настройки» (§127 п. 7, design.md
  // stage9-settings-page, Д5): кнопка строки при пустом наборе групп и ссылка
  // панели переводят в раздел «Терминал». Повод «groups» несёт сам адрес.
  // Присвоение `location.hash` пишет запись в историю — «назад» вернёт сюда.
  const openTerminalSettings = useCallback((hint?: TerminalSettingsHint) => {
    window.location.hash = hashOfSettingsReason(hint === "groups" ? "groups" : "terminal");
  }, []);
  const terminal = useTerminalSession({
    onNotice: handleTerminalNotice,
    onOpenSettings: openTerminalSettings,
  });
  // Рынки для страницы настроек спрашиваются в момент её открытия: значение,
  // пересчитываемое на каждый кадр потока, перерисовывало бы экран зря.
  const getLiveMarkets = useStableCallback(() =>
    liveMarketsOf(withoutHiddenRows(Object.values(store.getSnapshot().densities), hidden)),
  );
  // Layout-эффект, а не обычный: при прямом заходе на `#/settings` страница
  // строит черновик в обычном эффекте, и источник должен быть записан раньше.
  useLayoutEffect(() => {
    liveMarketsSource.current = getLiveMarkets;
    return () => {
      liveMarketsSource.current = () => [];
    };
  }, [liveMarketsSource, getLiveMarkets]);

  const handleSortChange = useCallback((key: DensitySortKey) => {
    setSort((previous) =>
      previous.key === key
        ? { key, direction: previous.direction === "asc" ? "desc" : "asc" }
        : { key, direction: defaultSortDirection(key) },
    );
  }, []);
  const handleToggleFilters = useCallback(() => setFiltersOpen((previous) => !previous), []);
  const handleOpenRow = useCallback((row: DensitySnapshot) => {
    setFocusNotesOnOpen(false);
    setOpenId(row.density_id);
  }, []);

  // Счётчики полосы фильтров. Счётчик у переключателя режима считает то, что
  // показано в этом режиме (review, пункт 4): строки плотностей в режиме
  // плотностей и в режиме карточек, ситуации в режиме ситуаций — иначе он
  // продолжал бы называть строки, которых на экране нет вовсе. Числа меняются
  // почти каждый кадр, поэтому уходят не пропсами, а в мини-хранилище: полоса
  // от них не перерисовывается, числа рисуют листья (`screen-count-leaves.tsx`).
  const currentCounts: ScreenCounts = {
    visible: mode === "situations" ? situationRowsComputed.rows.length : rows.length,
    total: mode === "situations" ? visibleSituations.length : allRows.length,
    states: rawCounts.states,
    venues: rawCounts.venues,
    newCount: cardsFreeze.newCount,
  };
  const [screenCounts] = useState(() => createScreenCounts(currentCounts));
  // Ключ намерения: в него входит всё, что меняет пользователь и не приходит из
  // потока (вид, фильтры, заморозка, личные минимумы, время до показа, избранное
  // для отбора, скрытые монеты, активность экрана). Сменился относительно прошлой
  // записи — счётчики показываются сразу (§84 п. 3); иначе поток прореживается
  // (§84 п. 2). Новый пользовательский вход добавляется сюда, а не обработчиком.
  const intentKey = useMemo(
    () =>
      JSON.stringify([
        mode,
        filters,
        cardsFreeze.frozen,
        displayMinimums,
        displayMinAge,
        favorites.state,
        [...hidden].sort(),
        active,
      ]),
    [
      mode,
      filters,
      cardsFreeze.frozen,
      displayMinimums,
      displayMinAge,
      favorites.state,
      hidden,
      active,
    ],
  );
  const lastIntentKey = useRef(intentKey);
  useLayoutEffect(() => {
    const intentChanged = lastIntentKey.current !== intentKey;
    lastIntentKey.current = intentKey;
    screenCounts.set(currentCounts, intentChanged ? { immediate: true } : undefined);
  });
  useEffect(() => () => screenCounts.dispose(), [screenCounts]);
  const stale = connectionStatus !== "open";

  return (
    <TerminalControlsContext.Provider value={terminal}>
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Исход копирования тикера — текстом в живой области верхней полосы
          каркаса (web-density-table «Отказ буфера обмена назван»): область
          смонтирована там всегда, сюда приходит только текст. */}
        {/* На других экранах исход копирования не показывается: копировали здесь. */}
        <CopyNoticePortal slot={noticeSlot} notice={active ? copyNotice : null} />

        <FilterPanel
          filters={filters}
          onChange={setFilters}
          displayMinimums={displayMinimums}
          displayMinAge={displayMinAge}
          onSaveDisplayMinAge={onSaveDisplayMinAge}
          onRemoveDisplayMinAge={onRemoveDisplayMinAge}
          onReloadDisplayMinAge={onReloadDisplayMinAge}
          onOpenDisplayMinimums={onOpenDisplayMinimums}
          venues={venues}
          counts={screenCounts}
          open={filtersOpen}
          onToggle={handleToggleFilters}
          mode={mode}
          sort={sort}
          onSortChange={handleSortChange}
          rowOrder={rowOrder}
          onRowOrderChange={setRowOrder}
          frozen={cardsFreeze.frozen}
          onToggleFreeze={cardsFreeze.toggle}
        />

        {pinNotice !== null ? (
          <DismissibleErrorBanner
            text={
              pinNotice === "gone"
                ? "Этого закрепления уже нет — список обновлён."
                : "Закрепление не сохранилось — попробуйте ещё раз."
            }
            onDismiss={() => setPinNotice(null)}
          />
        ) : null}

        {favoriteNotice !== null ? (
          <DismissibleErrorBanner
            text={favoriteToggleNoticeText(favoriteNotice)}
            onDismiss={() => setFavoriteNotice(null)}
          />
        ) : null}

        {hideNotice !== null ? (
          <DismissibleErrorBanner
            text={hideCoinNoticeText(hideNotice)}
            onDismiss={() => setHideNotice(null)}
          />
        ) : null}

        {cardPinNotice !== null ? (
          <DismissibleErrorBanner
            text={cardPinNotice.text}
            onDismiss={() => setCardPinNotice(null)}
          />
        ) : null}

        {/* Обёртка нужна льду §113: он лежит поверх области и не прокручивается с ней. */}
        <CardsFrostFrame mode={mode} frozen={cardsFreeze.frozen}>
          {/* `data-list-scroll` — по нему виртуальные таблица и карточки
              находят свою область прокрутки (`LIST_SCROLL_ATTRIBUTE`). */}
          <div
            data-list-scroll
            className={cn("min-h-0 flex-1 overflow-auto", stale && "opacity-80")}
          >
            <DensityListArea
              mode={mode}
              displayMinimums={displayMinimums}
              onPutDisplayMinimum={onPutDisplayMinimum}
              onRemoveDisplayMinimum={onRemoveDisplayMinimum}
              onReloadDisplayMinimums={onReloadDisplayMinimums}
              rows={rows}
              frozenCards={cardsFreeze.grid}
              lockedCards={visibleLockedCards}
              totalRows={allRows.length}
              situationRows={situationRowsComputed.rows}
              totalSituations={visibleSituations.length}
              unknownSituationCount={situationRowsComputed.unknownCount}
              sort={sort}
              rowOrder={rowOrder}
              instruments={instruments}
              wrapped={wrappedCoins}
              exchangeBases={exchangeBases}
              awaitingHello={run === null}
              favoritesUnavailable={favoritesUnavailable}
              onOpenRow={handleOpenRow}
              notedBases={notedBases}
              pinMatches={pinMatches}
              onOpenNotes={handleOpenNotes}
              onTogglePin={handleTogglePin}
              pendingPinIds={pendingPinIds}
              favoriteBases={favoriteBases}
              onToggleFavorite={handleToggleFavorite}
              pendingFavoriteBases={favorites.pendingBases}
              expandedMembers={expandedMembers}
              onToggleExpand={handleToggleExpand}
              onHideCoin={handleHideCoin}
              onCopyTicker={handleCopyTicker}
              selectedDensityId={openId}
              pinnedCards={pinnedCards}
              cardPinsStatus={cardPins.state.status}
              pendingCardPinKeys={cardPins.pendingKeys}
              cardPinExchangeBases={cardPinExchangeBases}
              onToggleCardPin={handleToggleCardPin}
            />
          </div>
        </CardsFrostFrame>

        <DensityDetailPanel
          wrapped={wrappedCoins}
          // Пока открыт другой экран (страница «Настройки» по ссылке панели),
          // модальная панель не должна закрывать его; выбранная плотность
          // остаётся, и «назад» возвращает панель.
          row={active ? openRow : null}
          state={detail.state}
          onRefresh={detail.refresh}
          onClose={handleCloseDetail}
          notesState={notes.state}
          onCreateNote={notes.create}
          onUpdateNoteText={notes.updateText}
          onDeleteNote={notes.remove}
          favoriteBases={favoriteBases}
          onToggleFavorite={handleToggleFavorite}
          pendingFavoriteBases={favorites.pendingBases}
          favoriteNotice={favoriteNotice}
          onHideCoin={handleHideCoin}
          hideNotice={hideNotice}
          actionNotice={copyNotice}
          focusNotes={focusNotesOnOpen}
          onCopyTicker={handleCopyTicker}
          onTogglePin={handleTogglePin}
          // Та же связка, что и у строки таблицы (web-density-detail
          // «Закрепление из панели»): `null` — список закреплений ещё не
          // прочитан или недоступен, строка не вправе утверждать «не
          // закреплено» — тем же приёмом, что `notedBases`/`favoriteBases`.
          pinMatch={openPinMatch}
          pinPending={openRow !== null && pendingPinIds.has(openRow.density_id)}
        />

        {/* Подвал по макету «Cards Final» (§88 п. 9): одна строка 24 px,
          число строк и лента исходов; лишнее обрезается, а не переносится. */}
        <footer className="flex h-6 shrink-0 items-center gap-4 overflow-hidden border-t border-border px-3 text-[11px] whitespace-nowrap text-muted-foreground tabular-nums">
          <span className="shrink-0 text-foreground">
            {footerCountLabel(mode, rows.length, situationRowsComputed.rows.length)}
          </span>
          <OutcomeRibbon outcomes={visibleOutcomes} wrapped={wrappedCoins} />
        </footer>
      </div>
    </TerminalControlsContext.Provider>
  );
}
